import { useMemo, useState } from 'react';

import {
  CalendarClock,
  DollarSign,
  MessagesSquare,
  ShieldAlert,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { formatarDinheiro } from '@/nucleo/dinheiro';
import { formatarData } from '@/nucleo/formato/datas';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import { StatCardSkeleton } from '@/ui/base/table-skeleton';
import { MoneyDisplay } from '@/ui/MoneyDisplay';

import { OPCOES_DE_STATUS_DA_MEDICAO, SELO_DA_MEDICAO, formatarCompetencia } from './apresentacao';
import { ChatComSmeModal } from './ChatComSmeModal';
import { useMedicoes, useResumoFinanceiro } from './dados';
import { SolicitarRevisaoModal } from './SolicitarRevisaoModal';
import {
  TAMANHO_PAGINA_PADRAO,
  type FiltroDeMedicoes,
  type MedicaoResumo,
  type StatusMedicao,
} from './contrato';

/**
 * Uma coluna em 375 px (regra de interface 4): em duas, o valor em reais de
 * cada indicador não cabe no cartão e sai cortado. Mesmo arranjo de
 * `cooperados/CooperadosPage.tsx`.
 */
const COLUNAS_DOS_INDICADORES = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

/**
 * `/credenciado/financeiro` — medições mensais do credenciado.
 *
 * KPIs no topo (últimos 12 meses) mais a lista de medições, com a
 * competência, o valor bruto, as deduções, o líquido e o estágio de cada
 * uma. Cada linha leva ao demonstrativo formal, em
 * `/credenciado/financeiro/:id`. Dado mockado — ver `dados.ts` e
 * `comum/mock.ts` para a justificativa.
 */
export function FinanceiroPage() {
  const navigate = useNavigate();
  const resumo = useResumoFinanceiro();

  const [status, setStatus] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHO_PAGINA_PADRAO);

  const filtro: FiltroDeMedicoes = {
    pagina,
    tamanho,
    status: (status || undefined) as StatusMedicao | undefined,
  };
  const medicoes = useMedicoes(filtro);

  const [medicaoParaRevisao, setMedicaoParaRevisao] = useState<MedicaoResumo | null>(null);
  const [medicaoParaChat, setMedicaoParaChat] = useState<MedicaoResumo | null>(null);

  const carregandoResumo = resumo.isLoading;
  const comErro = resumo.isError || medicoes.isError;

  const stats: StatItem[] = resumo.data
    ? [
        {
          title: 'Acumulado (12 meses)',
          value: formatarDinheiro(resumo.data.acumulado_12_meses),
          icon: TrendingUp,
          description: 'Valor bruto consolidado',
        },
        {
          title: 'Já recebido',
          value: formatarDinheiro(resumo.data.total_recebido),
          icon: Wallet,
          description: 'Medições pagas',
        },
        {
          title: 'A receber',
          value: formatarDinheiro(resumo.data.total_a_receber),
          icon: DollarSign,
          description: 'Em apuração ou aguardando ateste',
        },
        {
          title: 'Próximo crédito',
          value: resumo.data.proximo_credito_previsto_em
            ? formatarDinheiro(resumo.data.proximo_credito_valor)
            : '—',
          icon: CalendarClock,
          description: resumo.data.proximo_credito_previsto_em
            ? `Previsto para ${formatarData(resumo.data.proximo_credito_previsto_em)}`
            : 'Sem previsão informada',
        },
      ]
    : [];

  const campos: FilterFieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: OPCOES_DE_STATUS_DA_MEDICAO,
        width: 'w-full sm:w-[220px]',
      },
    ],
    [],
  );

  const colunas: DataTableColumn<MedicaoResumo>[] = [
    {
      id: 'competencia',
      header: 'Competência',
      // Link de verdade (regras de interface 5 e 9): os botões da coluna Ações
      // são controles focáveis dentro da linha, então a linha não pode ser
      // também um botão (`nested-interactive`). Com `linhaComElementoInterativo`
      // ela sai da ordem de tabulação e o teclado chega ao demonstrativo por
      // aqui; o mouse continua abrindo pela linha inteira.
      cell: (medicao) => (
        <Link
          to={`/credenciado/financeiro/${medicao.id}`}
          className="focus-visible:ring-ring rounded font-mono text-sm hover:underline focus:outline-hidden focus-visible:ring-2"
        >
          {formatarCompetencia(medicao.competencia)}
        </Link>
      ),
      sortKey: 'competencia',
    },
    {
      id: 'valor_bruto',
      header: 'Valor bruto',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (medicao) => <MoneyDisplay value={medicao.valor_bruto} />,
      sortKey: 'valor_bruto',
    },
    {
      id: 'total_dedutivos',
      header: 'Deduções',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (medicao) => (
        <MoneyDisplay value={medicao.total_dedutivos} className="text-muted-foreground" />
      ),
      sortKey: 'total_dedutivos',
    },
    {
      id: 'valor_liquido',
      header: 'Valor líquido',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (medicao) => <MoneyDisplay value={medicao.valor_liquido} className="font-semibold" />,
      sortKey: 'valor_liquido',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (medicao) => {
        const selo = SELO_DA_MEDICAO[medicao.status];
        return <StatusBadge status={selo.variante} label={selo.rotulo} />;
      },
      sortKey: 'status',
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: (medicao) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Solicitar revisão da medição de ${formatarCompetencia(medicao.competencia)}`}
            title="Solicitar revisão"
            onClick={(evento) => {
              evento.stopPropagation();
              setMedicaoParaRevisao(medicao);
            }}
          >
            <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Abrir mensagens sobre a medição de ${formatarCompetencia(medicao.competencia)}`}
            title="Mensagens com a SME"
            onClick={(evento) => {
              evento.stopPropagation();
              setMedicaoParaChat(medicao);
            }}
          >
            <MessagesSquare className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financeiro"
        description="Acompanhe suas medições mensais e os valores processados."
      />

      {comErro ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar o financeiro agora. Tente novamente em instantes."
          primaryAction={{
            label: 'Tentar de novo',
            onClick: () => {
              void resumo.refetch();
              void medicoes.refetch();
            },
          }}
        />
      ) : carregandoResumo ? (
        <div role="status" aria-live="polite" className={`grid gap-4 ${COLUNAS_DOS_INDICADORES}`}>
          {Array.from({ length: 4 }).map((_, indice) => (
            <StatCardSkeleton key={indice} />
          ))}
          <p className="sr-only">Carregando o financeiro.</p>
        </div>
      ) : (
        <>
          <StatsGrid stats={stats} columns={COLUNAS_DOS_INDICADORES} />

          <FilterBar
            fields={campos}
            values={{ status }}
            onChange={(_chave, valor) => {
              setStatus(valor);
              setPagina(1);
            }}
            onClear={() => {
              setStatus('');
              setPagina(1);
            }}
          />

          <DataTable
            columns={colunas}
            data={medicoes.data?.itens ?? []}
            isLoading={medicoes.isLoading}
            total={medicoes.data?.total}
            page={pagina}
            perPage={tamanho}
            onPageChange={setPagina}
            onPerPageChange={(novoTamanho) => {
              setTamanho(novoTamanho);
              setPagina(1);
            }}
            onRowClick={(medicao) => navigate(`/credenciado/financeiro/${medicao.id}`)}
            linhaComElementoInterativo
            emptyIcon={DollarSign}
            emptyTitle="Nenhuma medição encontrada"
            emptyDescription={
              status
                ? 'Nenhuma medição está nesse status. Tente outro filtro.'
                : 'Quando houver medição apurada, ela aparece aqui.'
            }
            ariaLabel="Lista de medições financeiras"
          />
        </>
      )}

      {medicaoParaRevisao && (
        <SolicitarRevisaoModal
          open
          onOpenChange={(aberto) => !aberto && setMedicaoParaRevisao(null)}
          medicaoId={medicaoParaRevisao.id}
          competencia={formatarCompetencia(medicaoParaRevisao.competencia)}
        />
      )}

      {medicaoParaChat && (
        <ChatComSmeModal
          open
          onOpenChange={(aberto) => !aberto && setMedicaoParaChat(null)}
          medicaoId={medicaoParaChat.id}
          competencia={formatarCompetencia(medicaoParaChat.competencia)}
        />
      )}
    </div>
  );
}

export default FinanceiroPage;
